import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuth } from '../../context/AuthContext';
import { ActiveAquariumContext } from '../../contexts/AquariumContext';
import { aquariumIdFromPath } from '../../config/navigationMatch';
import { ApiError } from '../../lib/apiClient';
import { useAiUsage, invalidateAiUsage } from '../../hooks/useAiUsage';
import { captureKeys, useCaptureSummary, useVoiceSettings } from '../../hooks/useCaptures';
import { useVoiceRecorder, isVoiceRecordingSupported, type RecordedMeta } from '../../hooks/useVoiceRecorder';
import { uploadVoiceCapture } from '../../services/captureService';
import { VoiceQueue, storageAvailable as checkStorage, type QueuedNote } from '../../services/voiceQueue';
import type { AnalysisMode, CaptureDto, VoiceSettingsDto } from '../../types/capture';
import { CaptureSheet } from './CaptureSheet';
import { VoiceConsentDialog } from './VoiceConsentDialog';

/**
 * Voice notes app-wide (docs/voiceFeature.md, UC-A/UC-6): the recorder lives
 * here, above the routes, so a recording survives navigation; the pinned bar
 * (CaptureDock, rendered by AppLayout), the capture sheet and the consent
 * dialog read this context. Stop uploads immediately; without a connection the
 * note goes to the offline queue. When an uploaded note is ready, the "Note
 * ready" banner appears on whatever page the user is on.
 */

export type CapturePhase = 'idle' | 'requesting' | 'recording' | 'paused' | 'uploading' | 'processing';

/** Error notices shown in the dock. */
export type CaptureNotice = 'denied' | 'unsupported' | 'noCredits' | 'uploadFailed' | 'storageUnavailable' | null;

export interface ReadyNotice {
  /** Notes that finished since the banner was last dismissed. */
  count: number;
  /** Suggestions to check in them. */
  itemCount: number;
  /** Recording time of the newest one. */
  recordedAt: string;
  failed: boolean;
}

export interface StartRecordingOptions {
  aquariumId?: string;
  /** Credits used up: record anyway, analyse later. */
  deferAnalysis?: boolean;
}

/**
 * The voice journal switch (VOICE_CAPTURE_ENABLED, GET /me/voice `enabled`):
 * besides voice notes it also gates the guided test session and emergency help,
 * which ship with it. 'loading' until the settings arrived; an error counts as off.
 */
export type VoiceJournalState = 'loading' | 'on' | 'off';

export interface CaptureContextValue {
  /** Signed in, voice notes enabled on the server and the browser can record. */
  available: boolean;
  /** The voice journal is switched on (independent of the browser's recording support). */
  voiceJournal: VoiceJournalState;
  settings: VoiceSettingsDto | undefined;
  phase: CapturePhase;
  elapsedMs: number;
  level: number;
  lowSignal: boolean;
  limitWarning: boolean;
  /** Tank the next/current note is for (route → active aquarium). */
  aquariumId: string | null;
  aquariumName: string | null;
  /** Language of the next recording (two-letter code). */
  language: string;
  cycleLanguage: () => void;
  /** Credits of one note right now (1 during the intro), undefined until loaded. */
  noteCredits: number | undefined;
  /** Translated cost caption ("3 AI credits per note · 120 left"), null until loaded. */
  costHint: string | null;
  /** "Text only, no AI": the next notes become journal text without the analysis (remembered per device). */
  textOnly: boolean;
  setTextOnly: (value: boolean) => void;
  startRecording: (opts?: StartRecordingOptions) => void;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  discardRecording: () => void;
  /** Hides the "Processing…" bar (the note keeps processing). */
  dismissProcessing: () => void;
  sheetOpen: boolean;
  openCaptureSheet: () => void;
  closeCaptureSheet: () => void;
  openConsent: (opts?: { thenRecord?: boolean }) => void;
  notice: CaptureNotice;
  dismissNotice: () => void;
  /** "Record anyway — analyse later" after the no-credits warning. */
  recordAnyway: () => void;
  /** "Write instead": the journal's new-entry form of the tank. */
  writeInstead: () => void;
  ready: ReadyNotice | null;
  dismissReady: () => void;
  queue: QueuedNote[];
  retryQueued: (id?: string) => void;
  discardQueued: (id: string) => void;
  storageAvailable: boolean;
  /** Captures waiting in "To review" (Capture tab and sidebar count). */
  toReview: number;
}

export const CaptureContext = createContext<CaptureContextValue | null>(null);

/** The capture context; throws outside CaptureProvider. */
export function useCapture(): CaptureContextValue {
  const value = useContext(CaptureContext);
  if (!value) throw new Error('useCapture must be used inside CaptureProvider');
  return value;
}

/** The capture context, or null when rendered without CaptureProvider (isolated tests). */
export function useOptionalCapture(): CaptureContextValue | null {
  return useContext(CaptureContext);
}

/** The voice journal switch; 'off' outside CaptureProvider. */
export function useVoiceJournal(): VoiceJournalState {
  return useContext(CaptureContext)?.voiceJournal ?? 'off';
}

export const VOICE_LANGUAGES = ['de', 'en', 'fr', 'es', 'it', 'nl', 'ja'] as const;

function baseLanguage(lang: string | undefined): string {
  const base = (lang ?? 'en').split('-')[0]!.toLowerCase();
  return (VOICE_LANGUAGES as readonly string[]).includes(base) ? base : 'en';
}

const READY_STATUSES = new Set(['review', 'failed']);
const TEXT_ONLY_KEY = 'reefguide-voice-text-only';

function readTextOnly(): boolean {
  try {
    return localStorage.getItem(TEXT_ONLY_KEY) === '1';
  } catch {
    return false;
  }
}

export function CaptureProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const userId = auth.state?.isAuthenticated === true ? (auth.state.user?.userId ?? null) : null;
  const signedIn = userId !== null;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeAquarium = useContext(ActiveAquariumContext);

  const settingsQuery = useVoiceSettings(signedIn);
  const settings = settingsQuery.data;
  const available = signedIn && settings?.enabled === true && isVoiceRecordingSupported();
  const voiceJournal: VoiceJournalState = !signedIn
    ? 'off'
    : settings
      ? settings.enabled === true ? 'on' : 'off'
      : settingsQuery.isError ? 'off' : 'loading';
  const summary = useCaptureSummary(signedIn && settings?.enabled === true);
  const { usage, exceeded } = useAiUsage();

  const routeAquariumId = aquariumIdFromPath(pathname);
  const [explicitAquariumId, setExplicitAquariumId] = useState<string | null>(null);
  const aquariumId = explicitAquariumId ?? routeAquariumId ?? activeAquarium?.activeAquariumId ?? null;
  const aquariumName = activeAquarium?.aquariums.find((a) => a.id === aquariumId)?.name ?? null;

  const [language, setLanguage] = useState(() => baseLanguage(i18n.language));
  useEffect(() => setLanguage(baseLanguage(i18n.language)), [i18n.language]);
  const cycleLanguage = useCallback(() => {
    setLanguage((current) => VOICE_LANGUAGES[(VOICE_LANGUAGES.indexOf(current as never) + 1) % VOICE_LANGUAGES.length]!);
  }, []);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const consentThenRecord = useRef(false);
  const [notice, setNotice] = useState<CaptureNotice>(null);
  const [uploading, setUploading] = useState(false);
  const [processingVisible, setProcessingVisible] = useState(false);
  const [tracked, setTracked] = useState<string[]>([]);
  const [ready, setReady] = useState<ReadyNotice | null>(null);
  const [queue, setQueue] = useState<QueuedNote[]>([]);
  const [storageOk, setStorageOk] = useState(true);
  const deferRef = useRef(false);
  const [textOnly, setTextOnlyState] = useState(readTextOnly);
  /** Mode of the recording in progress (fixed when it starts). */
  const analysisModeRef = useRef<AnalysisMode>('ai');
  const setTextOnly = useCallback((value: boolean) => {
    setTextOnlyState(value);
    try {
      localStorage.setItem(TEXT_ONLY_KEY, value ? '1' : '0');
    } catch {
      // Private mode: the choice lasts for this visit only.
    }
  }, []);
  const recordingAquariumRef = useRef<string | null>(null);
  /** A finished recording that could not be uploaded yet (credits used up / consent missing). */
  const pendingRef = useRef<{ blob: Blob; meta: RecordedMeta; aquariumId: string } | null>(null);

  // ── Offline queue of this user ──────────────────────────────────────────────
  const voiceQueue = useMemo(
    () =>
      userId
        ? new VoiceQueue({
            userId,
            onUploaded: (capture) => {
              setTracked((ids) => (ids.includes(capture.id) ? ids : [...ids, capture.id]));
              void queryClient.invalidateQueries({ queryKey: captureKeys.all });
            },
          })
        : null,
    [userId, queryClient],
  );

  useEffect(() => {
    if (!voiceQueue) {
      setQueue([]);
      return undefined;
    }
    const unsubscribe = voiceQueue.subscribe(setQueue);
    voiceQueue.start();
    return () => {
      unsubscribe();
      voiceQueue.stop();
    };
  }, [voiceQueue]);

  useEffect(() => {
    let cancelled = false;
    void checkStorage().then((ok) => {
      if (!cancelled) setStorageOk(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Upload ──────────────────────────────────────────────────────────────────
  const track = useCallback(
    (capture: CaptureDto) => {
      setTracked((ids) => (ids.includes(capture.id) ? ids : [...ids, capture.id]));
      setProcessingVisible(true);
      void queryClient.invalidateQueries({ queryKey: captureKeys.all });
      invalidateAiUsage(queryClient);
    },
    [queryClient],
  );

  const upload = useCallback(
    async (blob: Blob, meta: RecordedMeta, targetAquariumId: string, deferAnalysis: boolean) => {
      const idempotencyKey = crypto.randomUUID();
      const analysisMode = analysisModeRef.current;
      const enqueue = async (lastError: string): Promise<void> => {
        if (!voiceQueue || !storageOk) {
          setNotice(storageOk ? 'uploadFailed' : 'storageUnavailable');
          return;
        }
        await voiceQueue.enqueue({
          id: idempotencyKey,
          aquariumId: targetAquariumId,
          blob,
          mimeType: meta.mimeType,
          recordedAt: meta.recordedAt,
          durationMs: meta.durationMs,
          language,
          deferAnalysis,
          analysisMode,
          lastError,
        });
        setProcessingVisible(false);
      };

      setUploading(true);
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await enqueue('OFFLINE');
          return;
        }
        const capture = await uploadVoiceCapture({
          aquariumId: targetAquariumId,
          audio: blob,
          mimeType: meta.mimeType,
          recordedAt: meta.recordedAt,
          durationMs: meta.durationMs,
          language,
          deferAnalysis,
          analysisMode,
          idempotencyKey,
        });
        track(capture);
      } catch (err) {
        if (err instanceof ApiError && err.code === 'AI_BUDGET_EXCEEDED') {
          pendingRef.current = { blob, meta, aquariumId: targetAquariumId };
          setNotice('noCredits');
        } else if (err instanceof ApiError && err.code === 'VOICE_CONSENT_REQUIRED') {
          pendingRef.current = { blob, meta, aquariumId: targetAquariumId };
          consentThenRecord.current = false;
          setConsentOpen(true);
        } else if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429) {
          setNotice('uploadFailed');
        } else {
          await enqueue(err instanceof ApiError ? err.code : 'NETWORK');
        }
      } finally {
        setUploading(false);
      }
    },
    [language, storageOk, track, voiceQueue],
  );

  const recorder = useVoiceRecorder({
    maxDurationSec: settings?.maxDurationSec ?? 180,
    onRecorded: (blob, meta) => {
      const target = recordingAquariumRef.current;
      if (!target || blob.size === 0) return;
      void upload(blob, meta, target, deferRef.current);
    },
  });

  useEffect(() => {
    if (recorder.status === 'denied') setNotice('denied');
    else if (recorder.status === 'unsupported') setNotice('unsupported');
  }, [recorder.status]);

  // ── Ready notes → banner ───────────────────────────────────────────────────
  useEffect(() => {
    const recent = summary.data?.recent ?? [];
    if (tracked.length === 0 || recent.length === 0) return;
    const finished = recent.filter((r) => tracked.includes(r.id) && READY_STATUSES.has(r.status));
    if (finished.length === 0) return;
    setTracked((ids) => ids.filter((id) => !finished.some((f) => f.id === id)));
    setReady((prev) => {
      const newest = finished.reduce((a, b) => (a.recordedAt > b.recordedAt ? a : b));
      return {
        count: (prev?.count ?? 0) + finished.length,
        itemCount: (prev?.itemCount ?? 0) + finished.reduce((sum, f) => sum + f.itemCount, 0),
        recordedAt: newest.recordedAt,
        failed: (prev?.failed ?? false) || finished.some((f) => f.status === 'failed'),
      };
    });
    void queryClient.invalidateQueries({ queryKey: captureKeys.list() });
  }, [summary.data, tracked, queryClient]);

  // Every tracked note finished: the "Processing…" bar has done its job.
  useEffect(() => {
    if (tracked.length === 0 && !uploading && ready) setProcessingVisible(false);
  }, [tracked.length, uploading, ready]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const consentGiven = settings?.consent.given === true;

  const beginRecording = useCallback(
    (target: string, deferAnalysis: boolean) => {
      deferRef.current = deferAnalysis;
      analysisModeRef.current = textOnly ? 'transcript' : 'ai';
      recordingAquariumRef.current = target;
      setNotice(null);
      setSheetOpen(false);
      void recorder.start();
    },
    [recorder, textOnly],
  );

  const startRecording = useCallback(
    (opts: StartRecordingOptions = {}) => {
      if (recorder.status === 'recording' || recorder.status === 'paused' || recorder.status === 'requesting') return;
      if (opts.aquariumId) setExplicitAquariumId(opts.aquariumId);
      const target = opts.aquariumId ?? aquariumId;
      if (!target) {
        setSheetOpen(true);
        return;
      }
      recordingAquariumRef.current = target;
      if (!consentGiven) {
        consentThenRecord.current = true;
        deferRef.current = opts.deferAnalysis === true;
        setSheetOpen(false);
        setConsentOpen(true);
        return;
      }
      if (exceeded && !opts.deferAnalysis) {
        setSheetOpen(false);
        setNotice('noCredits');
        return;
      }
      beginRecording(target, opts.deferAnalysis === true);
    },
    [aquariumId, beginRecording, consentGiven, exceeded, recorder.status],
  );

  const recordAnyway = useCallback(() => {
    setNotice(null);
    const pending = pendingRef.current;
    if (pending) {
      pendingRef.current = null;
      void upload(pending.blob, pending.meta, pending.aquariumId, true);
      return;
    }
    const target = recordingAquariumRef.current ?? aquariumId;
    if (target) beginRecording(target, true);
  }, [aquariumId, beginRecording, upload]);

  const writeInstead = useCallback(() => {
    setNotice(null);
    setSheetOpen(false);
    setConsentOpen(false);
    pendingRef.current = null;
    const target = recordingAquariumRef.current ?? aquariumId;
    navigate(target ? `/aquariums/${encodeURIComponent(target)}/journal?new=1` : '/aquariums');
  }, [aquariumId, navigate]);

  const onConsentGiven = useCallback(() => {
    setConsentOpen(false);
    const pending = pendingRef.current;
    if (pending) {
      pendingRef.current = null;
      void upload(pending.blob, pending.meta, pending.aquariumId, deferRef.current);
      return;
    }
    if (consentThenRecord.current) {
      consentThenRecord.current = false;
      const target = recordingAquariumRef.current ?? aquariumId;
      if (!target) return;
      if (exceeded && !deferRef.current) {
        setNotice('noCredits');
        return;
      }
      beginRecording(target, deferRef.current);
    }
  }, [aquariumId, beginRecording, exceeded, upload]);

  const openConsent = useCallback((opts: { thenRecord?: boolean } = {}) => {
    consentThenRecord.current = opts.thenRecord === true;
    setConsentOpen(true);
  }, []);

  // ── Derived state ──────────────────────────────────────────────────────────
  let phase: CapturePhase = 'idle';
  if (recorder.status === 'requesting') phase = 'requesting';
  else if (recorder.status === 'recording') phase = 'recording';
  else if (recorder.status === 'paused') phase = 'paused';
  else if (uploading) phase = 'uploading';
  else if (processingVisible) phase = 'processing';

  const pricing = settings?.pricing;
  const noteCredits = pricing ? (pricing.introLeft > 0 ? pricing.introCredits : pricing.credits) : undefined;
  let costHint: string | null = null;
  if (pricing && textOnly) {
    costHint = String(t('capture.sheet.costHintTextOnly', { count: pricing.transcriptOnlyCredits }));
  } else if (pricing) {
    const budget = usage?.creditsBudget ?? null;
    const remaining = budget === null || !usage ? null : Math.max(0, Math.floor(budget - usage.creditsUsed));
    if (pricing.introLeft > 0) {
      costHint =
        remaining === null
          ? String(t('capture.sheet.costHintIntroUnlimited', { introLeft: pricing.introLeft, count: pricing.introCredits }))
          : String(t('capture.sheet.costHintIntro', { introLeft: pricing.introLeft, remaining, count: pricing.introCredits }));
    } else {
      costHint =
        remaining === null
          ? String(t('capture.sheet.costHintUnlimited', { credits: pricing.credits }))
          : String(t('capture.sheet.costHint', { credits: pricing.credits, remaining }));
    }
  }

  const value: CaptureContextValue = {
    available,
    voiceJournal,
    settings,
    phase,
    elapsedMs: recorder.elapsedMs,
    level: recorder.level,
    lowSignal: recorder.lowSignal,
    limitWarning: recorder.limitWarning,
    aquariumId,
    aquariumName,
    language,
    cycleLanguage,
    noteCredits,
    costHint,
    textOnly,
    setTextOnly,
    startRecording,
    stopRecording: recorder.stop,
    pauseRecording: recorder.pause,
    resumeRecording: recorder.resume,
    discardRecording: recorder.discard,
    dismissProcessing: () => setProcessingVisible(false),
    sheetOpen,
    openCaptureSheet: () => setSheetOpen(true),
    closeCaptureSheet: () => setSheetOpen(false),
    openConsent,
    notice,
    dismissNotice: () => setNotice(null),
    recordAnyway,
    writeInstead,
    ready,
    dismissReady: () => setReady(null),
    queue,
    retryQueued: (id) => {
      void voiceQueue?.retry(id);
    },
    discardQueued: (id) => {
      void voiceQueue?.discard(id);
    },
    storageAvailable: storageOk,
    toReview: summary.data?.toReview ?? 0,
  };

  return (
    <CaptureContext.Provider value={value}>
      {children}
      {signedIn && (
        <>
          <CaptureSheet />
          <VoiceConsentDialog
            isOpen={consentOpen}
            recordAfter={consentThenRecord.current}
            onClose={() => {
              setConsentOpen(false);
              pendingRef.current = null;
            }}
            onGiven={onConsentGiven}
            onWriteInstead={writeInstead}
          />
        </>
      )}
    </CaptureContext.Provider>
  );
}
